/**
 * DocumentThemes — Browser-safe theme presets for rendered markdown preview.
 *
 * Mirrors the DOCX_THEME_PRESETS from convertr-lib/DocxThemeAdapter
 * so the preview matches what the DOCX export produces.
 *
 * Composes:
 * - theemr-lib (ThemeSettings shape): Theme property structure
 * - convertr-lib (DocxThemeAdapter): Same presets used for DOCX export
 * - stylr-lib (conceptual): Visual theme editing
 *
 * Each preset defines colors, typography (fontFamily, fontSize, fontWeight,
 * lineHeight), and spacing. These map directly to CSS properties for the
 * rendered markdown preview.
 */

// ── Theme Settings Shape (matches theemr-lib ThemeSettings) ──

export interface DocumentThemeSettings {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    error: string;
    warning: string;
    success: string;
    info: string;
  };
  typography: {
    fontFamily: string;
    fontSize: {
      xs: string; sm: string; base: string; lg: string;
      xl: string; '2xl': string; '3xl': string;
    };
    fontWeight: {
      light: number; normal: number; medium: number;
      semibold: number; bold: number;
    };
    lineHeight: {
      tight: number; normal: number; relaxed: number;
    };
  };
  spacing: {
    xs: string; sm: string; md: string; lg: string;
    xl: string; '2xl': string;
  };
  borderRadius: {
    none: string; sm: string; md: string; lg: string; full: string;
  };
}

export interface DocumentTheme {
  id: string;
  name: string;
  description: string;
  settings: DocumentThemeSettings;
}

// ── Default spacing and border radius (shared) ──

const defaultSpacing = {
  xs: '0.25rem', sm: '0.5rem', md: '1rem', lg: '1.5rem', xl: '2rem', '2xl': '3rem',
};

const defaultBorderRadius = {
  none: '0', sm: '0.25rem', md: '0.5rem', lg: '1rem', full: '9999px',
};

// ── Theme Presets ──
// Mirrors convertr-lib/DocxThemeAdapter DOCX_THEME_PRESETS

export const DOCUMENT_THEMES: DocumentTheme[] = [
  {
    id: 'editor-dark',
    name: 'Editor Dark',
    description: 'Dark theme matching the editor background',
    settings: {
      colors: {
        primary: '#8b8bf0', secondary: '#6c6cf0', accent: '#a78bfa',
        background: '#0f0f1e', surface: '#1a1a2e', text: '#e0e0f0',
        textSecondary: '#8888aa', error: '#f87171', warning: '#fbbf24',
        success: '#34d399', info: '#60a5fa',
      },
      typography: {
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        fontSize: { xs: '0.75rem', sm: '0.875rem', base: '1rem', lg: '1.125rem', xl: '1.25rem', '2xl': '1.5rem', '3xl': '1.875rem' },
        fontWeight: { light: 300, normal: 400, medium: 500, semibold: 600, bold: 700 },
        lineHeight: { tight: 1.25, normal: 1.6, relaxed: 1.8 },
      },
      spacing: defaultSpacing,
      borderRadius: defaultBorderRadius,
    },
  },
  {
    id: 'corporate',
    name: 'Corporate Standard',
    description: 'Clean business style — Arial, blue headings',
    settings: {
      colors: {
        primary: '#003366', secondary: '#336699', accent: '#0066CC',
        background: '#FFFFFF', surface: '#F5F5F5', text: '#333333',
        textSecondary: '#666666', error: '#CC0000', warning: '#FF9900',
        success: '#339933', info: '#0066CC',
      },
      typography: {
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: { xs: '0.75rem', sm: '0.875rem', base: '1rem', lg: '1.125rem', xl: '1.25rem', '2xl': '1.5rem', '3xl': '1.875rem' },
        fontWeight: { light: 300, normal: 400, medium: 500, semibold: 600, bold: 700 },
        lineHeight: { tight: 1.25, normal: 1.5, relaxed: 1.75 },
      },
      spacing: defaultSpacing,
      borderRadius: defaultBorderRadius,
    },
  },
  {
    id: 'modern',
    name: 'Modern Minimal',
    description: 'Inter, dark slate headings, minimal aesthetic',
    settings: {
      colors: {
        primary: '#1E293B', secondary: '#475569', accent: '#3B82F6',
        background: '#FFFFFF', surface: '#F8FAFC', text: '#0F172A',
        textSecondary: '#64748B', error: '#EF4444', warning: '#F59E0B',
        success: '#22C55E', info: '#3B82F6',
      },
      typography: {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: { xs: '0.75rem', sm: '0.875rem', base: '1rem', lg: '1.125rem', xl: '1.375rem', '2xl': '1.625rem', '3xl': '2rem' },
        fontWeight: { light: 300, normal: 400, medium: 500, semibold: 600, bold: 700 },
        lineHeight: { tight: 1.3, normal: 1.6, relaxed: 1.8 },
      },
      spacing: defaultSpacing,
      borderRadius: defaultBorderRadius,
    },
  },
  {
    id: 'classic',
    name: 'Classic Serif',
    description: 'Georgia, warm tones, traditional editorial',
    settings: {
      colors: {
        primary: '#8B4513', secondary: '#A0522D', accent: '#CD853F',
        background: '#FFFEF5', surface: '#FFF8E7', text: '#2C1810',
        textSecondary: '#8B7355', error: '#B22222', warning: '#DAA520',
        success: '#228B22', info: '#4682B4',
      },
      typography: {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: { xs: '0.75rem', sm: '0.875rem', base: '1.0625rem', lg: '1.1875rem', xl: '1.375rem', '2xl': '1.625rem', '3xl': '2rem' },
        fontWeight: { light: 300, normal: 400, medium: 500, semibold: 600, bold: 700 },
        lineHeight: { tight: 1.3, normal: 1.6, relaxed: 1.85 },
      },
      spacing: defaultSpacing,
      borderRadius: defaultBorderRadius,
    },
  },
  {
    id: 'trusted-associates',
    name: 'Trusted Associates',
    description: 'Deep navy & teal — Playfair Display editorial authority',
    settings: {
      colors: {
        primary: '#1B2A4A', secondary: '#4A5568', accent: '#2B8A7E',
        background: '#FFFFFF', surface: '#F7F8FA', text: '#1A202C',
        textSecondary: '#4A5568', error: '#C53030', warning: '#D69E2E',
        success: '#38A169', info: '#3182CE',
      },
      typography: {
        fontFamily: 'Playfair Display, Georgia, serif',
        fontSize: { xs: '0.75rem', sm: '0.8125rem', base: '0.9375rem', lg: '1.0625rem', xl: '1.25rem', '2xl': '1.5rem', '3xl': '2rem' },
        fontWeight: { light: 300, normal: 400, medium: 500, semibold: 600, bold: 700 },
        lineHeight: { tight: 1.3, normal: 1.65, relaxed: 1.8 },
      },
      spacing: defaultSpacing,
      borderRadius: defaultBorderRadius,
    },
  },
  {
    id: 'w5',
    name: 'W-5',
    description: 'Deep indigo — Source Serif for contemplative long-form',
    settings: {
      colors: {
        primary: '#312E81', secondary: '#6D28D9', accent: '#F59E0B',
        background: '#FFFFFF', surface: '#FAFAFE', text: '#1E1B4B',
        textSecondary: '#4C4578', error: '#B91C1C', warning: '#D97706',
        success: '#059669', info: '#4F46E5',
      },
      typography: {
        fontFamily: 'Source Serif 4, Georgia, serif',
        fontSize: { xs: '0.75rem', sm: '0.875rem', base: '1rem', lg: '1.125rem', xl: '1.375rem', '2xl': '1.625rem', '3xl': '2.25rem' },
        fontWeight: { light: 300, normal: 400, medium: 500, semibold: 600, bold: 700 },
        lineHeight: { tight: 1.25, normal: 1.75, relaxed: 1.9 },
      },
      spacing: defaultSpacing,
      borderRadius: defaultBorderRadius,
    },
  },
];

/** Get a theme by ID, falls back to editor-dark */
export function getDocumentTheme(id: string): DocumentTheme {
  return DOCUMENT_THEMES.find(t => t.id === id) ?? DOCUMENT_THEMES[0]!;
}

// ── Markdown → HTML Renderer ──

/**
 * Simple markdown-to-HTML converter for browser preview.
 * Covers the same elements as convertr-lib's MarkdownToDocxPlugin:
 * headings, bold, italic, code, blockquotes, lists, links, hr, tables.
 */
export function markdownToHtml(markdown: string): string {
  const lines = markdown.split('\n');
  const html: string[] = [];
  let inCodeBlock = false;
  let codeBlockContent: string[] = [];
  let inList: 'ul' | 'ol' | null = null;

  const closeList = () => {
    if (inList) {
      html.push(inList === 'ul' ? '</ul>' : '</ol>');
      inList = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;

    // Code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        html.push(`<pre class="md-code-block"><code>${codeBlockContent.join('\n')}</code></pre>`);
        codeBlockContent = [];
        inCodeBlock = false;
      } else {
        closeList();
        inCodeBlock = true;
      }
      continue;
    }
    if (inCodeBlock) {
      codeBlockContent.push(escapeHtml(line));
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim()) || /^\*\*\*+$/.test(line.trim())) {
      closeList();
      html.push('<hr class="md-hr" />');
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      closeList();
      const level = headingMatch[1]!.length;
      html.push(`<h${level} class="md-h${level}">${inlineFormat(headingMatch[2]!)}</h${level}>`);
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      closeList();
      html.push(`<blockquote class="md-blockquote">${inlineFormat(line.slice(2))}</blockquote>`);
      continue;
    }

    // Unordered list
    const ulMatch = line.match(/^(\s*)[-*+]\s+(.+)$/);
    if (ulMatch) {
      if (inList !== 'ul') {
        closeList();
        html.push('<ul class="md-list">');
        inList = 'ul';
      }
      const indent = Math.floor(ulMatch[1]!.length / 2);
      html.push(`<li class="md-li" style="margin-left:${indent * 1.5}rem">${inlineFormat(ulMatch[2]!)}</li>`);
      continue;
    }

    // Ordered list
    const olMatch = line.match(/^(\s*)\d+\.\s+(.+)$/);
    if (olMatch) {
      if (inList !== 'ol') {
        closeList();
        html.push('<ol class="md-list">');
        inList = 'ol';
      }
      const indent = Math.floor(olMatch[1]!.length / 2);
      html.push(`<li class="md-li" style="margin-left:${indent * 1.5}rem">${inlineFormat(olMatch[2]!)}</li>`);
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      closeList();
      continue;
    }

    // Paragraph
    closeList();
    html.push(`<p class="md-paragraph">${inlineFormat(line)}</p>`);
  }

  closeList();
  if (inCodeBlock) {
    html.push(`<pre class="md-code-block"><code>${codeBlockContent.join('\n')}</code></pre>`);
  }

  return html.join('\n');
}

/** Format inline markdown: bold, italic, code, links */
function inlineFormat(text: string): string {
  let result = escapeHtml(text);
  // Bold italic: ***text***
  result = result.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  // Bold: **text**
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic: *text*
  result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Inline code: `code`
  result = result.replace(/`([^`]+)`/g, '<code class="md-inline-code">$1</code>');
  // Links: [text](url)
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a class="md-link" href="$2">$1</a>');
  return result;
}

/** Escape HTML entities */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Theme → CSS Mapper ──

/**
 * Generate a CSS string from a DocumentThemeSettings that styles
 * the rendered markdown preview. This maps theemr properties to
 * CSS in the same way DocxThemeAdapter maps them to DOCX styles.
 */
export function themeToCSS(settings: DocumentThemeSettings): string {
  const t = settings.typography;
  const c = settings.colors;
  const s = settings.spacing;

  return `
    .md-preview {
      font-family: ${t.fontFamily};
      font-size: ${t.fontSize.base};
      font-weight: ${t.fontWeight.normal};
      line-height: ${t.lineHeight.normal};
      color: ${c.text};
      background: ${c.background};
      padding: ${s.lg} ${s.xl};
      max-width: 48rem;
    }
    .md-preview .md-h1 {
      font-size: ${t.fontSize['3xl']};
      font-weight: ${t.fontWeight.bold};
      line-height: ${t.lineHeight.tight};
      color: ${c.primary};
      margin: ${s.xl} 0 ${s.md} 0;
      border-bottom: 1px solid ${c.surface};
      padding-bottom: ${s.sm};
    }
    .md-preview .md-h2 {
      font-size: ${t.fontSize['2xl']};
      font-weight: ${t.fontWeight.bold};
      line-height: ${t.lineHeight.tight};
      color: ${c.primary};
      margin: ${s.lg} 0 ${s.sm} 0;
    }
    .md-preview .md-h3 {
      font-size: ${t.fontSize.xl};
      font-weight: ${t.fontWeight.semibold};
      line-height: ${t.lineHeight.tight};
      color: ${c.primary};
      margin: ${s.md} 0 ${s.xs} 0;
    }
    .md-preview .md-h4 {
      font-size: ${t.fontSize.lg};
      font-weight: ${t.fontWeight.semibold};
      line-height: ${t.lineHeight.tight};
      color: ${c.secondary};
      margin: ${s.md} 0 ${s.xs} 0;
    }
    .md-preview .md-h5 {
      font-size: ${t.fontSize.base};
      font-weight: ${t.fontWeight.semibold};
      color: ${c.secondary};
      margin: ${s.sm} 0 ${s.xs} 0;
    }
    .md-preview .md-h6 {
      font-size: ${t.fontSize.sm};
      font-weight: ${t.fontWeight.semibold};
      color: ${c.secondary};
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin: ${s.sm} 0 ${s.xs} 0;
    }
    .md-preview .md-paragraph {
      margin: 0 0 ${s.md} 0;
      line-height: ${t.lineHeight.relaxed};
    }
    .md-preview .md-blockquote {
      border-left: 3px solid ${c.textSecondary};
      padding: ${s.sm} ${s.md};
      margin: ${s.sm} 0 ${s.md} 0;
      color: ${c.textSecondary};
      background: ${c.surface};
      font-style: italic;
    }
    .md-preview .md-code-block {
      background: ${c.surface};
      border: 1px solid ${c.textSecondary}33;
      border-radius: ${settings.borderRadius.sm};
      padding: ${s.md};
      margin: ${s.sm} 0 ${s.md} 0;
      overflow-x: auto;
      font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
      font-size: ${t.fontSize.sm};
      line-height: ${t.lineHeight.normal};
    }
    .md-preview .md-inline-code {
      background: ${c.surface};
      border: 1px solid ${c.textSecondary}22;
      border-radius: ${settings.borderRadius.sm};
      padding: 0.1em 0.35em;
      font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
      font-size: 0.9em;
    }
    .md-preview .md-link {
      color: ${c.accent || c.primary};
      text-decoration: underline;
      text-decoration-color: ${c.accent || c.primary}66;
      text-underline-offset: 2px;
    }
    .md-preview .md-link:hover {
      text-decoration-color: ${c.accent || c.primary};
    }
    .md-preview .md-hr {
      border: none;
      border-top: 1px solid ${c.textSecondary}44;
      margin: ${s.lg} 0;
    }
    .md-preview .md-list {
      margin: 0 0 ${s.md} 0;
      padding-left: ${s.lg};
    }
    .md-preview .md-li {
      margin: ${s.xs} 0;
      line-height: ${t.lineHeight.relaxed};
    }
    .md-preview strong {
      font-weight: ${t.fontWeight.bold};
    }
    .md-preview em {
      font-style: italic;
    }
  `;
}
